import { useEffect, useState } from 'react';

import { api } from '../../api';
import { go } from '../../App';
import { useLoad } from '../../data';
import { Loading, PageHead, Panel, Stars } from '../../ui';
import { Account } from '../../types';

// Business profile (canon 09 supplier "Business profile" · Form; §4.2 "Business").
// The write half of the account, through the ONE PUT /v1/account — E2.3's
// onboarding endpoint, the same write organic onboarding (S11) and Eva's
// business_profile tool use. Beyond the name and timezone (S10), S11 adds the
// canon-11 step-3 "business basics": what the supplier does (service categories,
// from the product taxonomy), where they leave from, and the languages they
// speak. The phone (identity) and currency (ledger) are shown but not editable.

const ZONES = [
  'Europe/Athens', 'Europe/Nicosia', 'Europe/Bucharest', 'Europe/Sofia', 'Europe/Istanbul',
  'Europe/Rome', 'Europe/Berlin', 'Europe/Paris', 'Europe/Madrid', 'Europe/London', 'UTC',
];
const CATEGORIES: { key: string; label: string }[] = [
  { key: 'experience', label: 'Experiences' },
  { key: 'transfer', label: 'Transfers' },
];
const LANGUAGES = ['Greek', 'English', 'German', 'French', 'Italian', 'Spanish', 'Russian', 'Dutch'];

export function Profile() {
  const { data, error, reload } = useLoad<Account>('/account');
  const [name, setName] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [place, setPlace] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Seed the editable state once the account has loaded.
  useEffect(() => {
    if (data && name === null) {
      setName(data.name);
      setTimezone(data.timezone);
      setCategories(data.categories ?? []);
      setPlace(data.location?.place ?? '');
      setLanguages(data.languages ?? []);
    }
  }, [data, name]);

  const zones = timezone && !ZONES.includes(timezone) ? [timezone, ...ZONES] : ZONES;
  const nameOk = (name ?? '').trim().length > 0 && (name ?? '').trim().length <= 120;
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const sameSet = (a: string[], b?: string[]) => a.length === (b ?? []).length && a.every((x) => (b ?? []).includes(x));
  const changed = data
    ? name?.trim() !== data.name ||
      timezone !== data.timezone ||
      !sameSet(categories, data.categories) ||
      place.trim() !== (data.location?.place ?? '') ||
      !sameSet(languages, data.languages)
    : false;

  const save = async () => {
    if (!nameOk || name === null) return;
    setBusy(true);
    setSaveError('');
    setSaved(false);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        timezone: timezone ?? undefined,
        categories,
        languages,
      };
      if (place.trim()) body.location = { place: place.trim() };
      await api('PUT', '/account', body);
      await reload();
      setSaved(true);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead title="Business profile" sub="How hotels see you, what you do, and the clock your day runs on." />

      {data === null ? (
        error ? <p className="error-line">{error}</p> : <Loading />
      ) : (
        <>
          {/* The review aggregate the hotels see (DECISION-056) — read from the
              SAME account record, so it is always in step with the Reviews
              screen. A quiet line here; the full list lives one click away. */}
          {data.rating && data.rating.count > 0 && (
            <p className="hint" style={{ marginTop: -8, marginBottom: 16 }}>
              <Stars rating={data.rating.average} /> {data.rating.average.toFixed(1)} · from{' '}
              {data.rating.count} review{data.rating.count === 1 ? '' : 's'} ·{' '}
              <a href="#/reviews" onClick={(e) => { e.preventDefault(); go('/reviews'); }}>see all</a>
            </p>
          )}

          <Panel title="Your business">
            <div className="field">
              <label htmlFor="name">Business name</label>
              <input id="name" value={name ?? ''} maxLength={120} onChange={(e) => { setName(e.target.value); setSaved(false); }} />
              <p className="hint" style={{ marginTop: 6 }}>The name hotels see when they connect and book you.</p>
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label>What you do</label>
              <div>
                {CATEGORIES.map((c) => (
                  <button key={c.key} type="button" className={`reason ${categories.includes(c.key) ? 'selected' : ''}`} onClick={() => { setCategories(toggle(categories, c.key)); setSaved(false); }}>
                    {c.label}
                    {categories.includes(c.key) && <span className="disc" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label htmlFor="place">Where you leave from</label>
              <input id="place" value={place} maxLength={120} placeholder="e.g. Old Port, Chania" onChange={(e) => { setPlace(e.target.value); setSaved(false); }} />
            </div>

            <div className="field" style={{ marginTop: 16 }}>
              <label>Languages you speak</label>
              <div>
                {LANGUAGES.map((l) => (
                  <button key={l} type="button" className={`reason ${languages.includes(l) ? 'selected' : ''}`} onClick={() => { setLanguages(toggle(languages, l)); setSaved(false); }}>
                    {l}
                    {languages.includes(l) && <span className="disc" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="field" style={{ marginTop: 16, maxWidth: 320 }}>
              <label htmlFor="tz">Time zone</label>
              <select id="tz" value={timezone ?? ''} onChange={(e) => { setTimezone(e.target.value); setSaved(false); }}>
                {zones.map((z) => (<option key={z} value={z}>{z}</option>))}
              </select>
              <p className="hint" style={{ marginTop: 6 }}>Every time you see — a request's start, a job's clock — is shown in this zone.</p>
            </div>
          </Panel>

          <Panel title="Fixed for now">
            <div className="kv">
              {data.currency && (
                <div className="kvrow"><span className="k">Currency</span><span className="v mono">{data.currency}</span></div>
              )}
              <div className="kvrow"><span className="k">Account</span><span className="v mono">{data.id}</span></div>
            </div>
            <p className="hint" style={{ marginTop: 8 }}>
              Your phone (how you sign in) and currency aren't changed here — message the hotel that
              invited you, or Eva, if either needs to change.
            </p>
          </Panel>

          {saveError && <p className="error-line" role="alert">{saveError}</p>}

          <div className="form-foot">
            <span className="hint" style={{ flex: 1, alignSelf: 'center' }}>{saved && !changed ? 'Saved.' : ' '}</span>
            <button className="btn" onClick={save} style={{ flex: 'none', minWidth: 160 }} disabled={busy || !nameOk || !changed}>
              {busy ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
